import Vue from 'vue'
import { useMutation } from '@tanstack/vue-query'
import pluralize from 'pluralize'

import type {
  Undefined,
  Nullable,
  Pagination,
  Paginator,
  ActivityGroup,
  ActivityGroupActivity,
  ActivityId,
  ActivityGroupId,
  ActivityGroupActivityId,
} from '@/types'
import { getCurrentInstance, mountVueToBody } from '@/util'
import {
  useActivityService,
  useActivityGroupService,
  useLoading,
  useConfirm,
  usePrompt,
  useDialogify,
} from '@/use'
import type { InitialActivityGroupForCreateActivityGroupForm } from '@/components/ActivityGroupForm/CreateActivityGroupForm/CreateActivityGroupForm.types'
import DialogPagination from '@/components/DialogPagination/DialogPagination.vue'

export default function useActivityGroupCRUD() {
  const instance = getCurrentInstance()
  const h = instance.proxy.$createElement
  const { service: activityService } = useActivityService()
  const { service: activityGroupService } = useActivityGroupService()
  const { setLoading } = useLoading()
  const { confirm } = useConfirm()
  const { prompt } = usePrompt()
  const { dialogify } = useDialogify()

  async function create(
    initial?: InitialActivityGroupForCreateActivityGroupForm
  ): Promise<Nullable<ActivityGroup>> {
    const { default: options } = await import(
      '@/components/ActivityGroupForm/CreateActivityGroupFormDialog.vue'
    )
    return new Promise((resolve) => {
      const c = new Vue({
        extends: options,
        parent: instance.proxy.$root,
        propsData: { initial },
      })
      const destroy = mountVueToBody(c)
      c.$once('success', (activityGroup: ActivityGroup) => {
        resolve(activityGroup)
        destroy()
      })
      c.$once('fail', () => {
        resolve(null)
        destroy()
      })
    })
  }

  async function edit(id: ActivityGroupId): Promise<Nullable<ActivityGroup>> {
    setLoading(true)
    try {
      const activityGroup = await activityGroupService.get(id)
      const { default: options } = await import(
        '@/components/ActivityGroupForm/EditActivityGroupFormDialog.vue'
      )
      return new Promise((resolve) => {
        const c = new Vue({
          extends: options,
          parent: instance.proxy.$root,
          propsData: { activityGroup },
        })
        const destroy = mountVueToBody(c)
        c.$once('success', (activityGroup: ActivityGroup) => {
          resolve(activityGroup)
          destroy()
        })
        c.$once('fail', () => {
          resolve(null)
          destroy()
        })
      })
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  async function del(id: ActivityGroupId) {
    if (
      (await confirm('Are you sure you would like to delete this block?')) ===
      false
    ) {
      return
    }
    await activityGroupService.delete(id)
  }

  const { mutateAsync: delManyMutateAsync } = useMutation({
    mutationFn: ({ ids }: { ids: ActivityGroupId[] }) =>
      activityGroupService.deleteMany(ids),
  })

  async function delMany(
    ids: ActivityGroupId[]
  ): Promise<Nullable<ActivityGroupId[]>> {
    const confirmed = await confirm(
      `Are you sure you want to delete ${ids.length} ${pluralize(
        'block',
        ids.length
      )}`
    )
    if (confirmed === false) return null
    return delManyMutateAsync({ ids })
  }

  const { mutateAsync: copyMutateAsync } = useMutation({
    mutationFn: ({ id, name }: { id: ActivityGroupId; name: string }) =>
      activityGroupService.copy(id, name),
  })

  async function copy(
    id: ActivityGroupId,
    initial: string
  ): Promise<Undefined<ActivityGroupId>> {
    const name = await prompt({
      title: 'New Block Name',
      label: 'Name',
      initial: `${initial} [COPY]`,
    })
    if (name === undefined) {
      return undefined
    }
    setLoading(true)
    try {
      const result = await copyMutateAsync({ id, name })
      return result
    } finally {
      setLoading(false)
    }
  }

  const { mutateAsync: copyManyMutateAsync } = useMutation({
    mutationFn: ({ ids }: { ids: ActivityGroupId[] }) =>
      activityGroupService.copyMany(ids),
  })

  async function copyMany(ids: ActivityGroupId[]): Promise<ActivityGroupId[]> {
    const result = await copyManyMutateAsync({ ids })
    return result
  }

  async function editActivity(
    relation: ActivityGroupActivityId,
    id: ActivityId,
    paginator: Nullable<Paginator> = null
  ): Promise<Nullable<ActivityGroupActivity>> {
    setLoading(true)
    try {
      const activity = await activityService.get(id, { relations: true })
      const { prev, next } = paginator ?? { prev: null, next: null }
      const pagination: Nullable<Pagination> =
        prev !== null || next !== null
          ? { hasPrev: prev !== null, hasNext: next !== null }
          : null
      const { default: options } = await import(
        '@/modules/activity-group/components/EditActivityGroupActivityActivity/EditActivityGroupActivityActivity.vue'
      )
      const EditRoomActivityForm = Vue.extend({ extends: options })
      const updated = await new Promise<Nullable<ActivityGroupActivity>>(
        (resolve) => {
          const { destroyer } = dialogify(
            [
              h(EditRoomActivityForm, {
                props: { relation, activity },
                on: {
                  success: async (value: ActivityGroupActivity) => {
                    resolve(value)
                    destroyer()
                  },
                  fail: () => {
                    resolve(null)
                    destroyer()
                  },
                },
              }),
              pagination !== null
                ? h(DialogPagination, {
                    props: { pagination },
                    on: {
                      next: () => {
                        if (next !== null) {
                          next()
                          destroyer()
                        }
                      },
                      prev: () => {
                        if (prev !== null) {
                          prev()
                          destroyer()
                        }
                      },
                    },
                  })
                : null,
            ],
            { title: `Edit Room Activity "${activity.name}"`, size: 'xl' },
            { hide: () => resolve(null) }
          )
        }
      )
      return updated
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    create,
    edit,
    del,
    delMany,
    copy,
    copyMany,
    editActivity,
  }
}
